import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import type { CoachNotification, CoachProfile } from '../services/types';
import { CoachNotificationService } from '../services/coachNotificationService';
import { NutritionService } from '../services/nutritionService';
import type { AgentInfo } from '../services/nutritionService';
import { getImageUrl } from '../services/api';

const DEFAULT_COACH: CoachProfile = {
  name: 'Coach Nova',
  tagline: 'AI треньор за сила и постоянство',
  avatarUrl: '/img/task_01kjn79xvaebx94rsshgtetxj9_1772386336_img_1.webp',
};

const CoachNotifications: React.FC = () => {
  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('trainer');
  const [coach, setCoach] = useState<CoachProfile>(DEFAULT_COACH);
  const [notifications, setNotifications] = useState<CoachNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    NutritionService.getAgentsTeam()
      .then((res) => setAgents(res.agents))
      .catch(() => {
        setAgents([
          { key: 'trainer', displayName: 'Coach Nova', tagline: 'AI треньор за сила и постоянство', avatarUrl: '/img/task_01kjn79xvaebx94rsshgtetxj9_1772386336_img_1.webp', isActive: true },
          { key: 'nutritionist', displayName: 'Coach Nora', tagline: 'AI нутриционист за хранене и диети', avatarUrl: null, isActive: true },
        ]);
      });
  }, []);

  const loadNotifications = async (agentType: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await CoachNotificationService.getNotifications(agentType);
      setCoach(response.coach || DEFAULT_COACH);
      setNotifications(response.notifications);
    } catch (err) {
      console.error(err);
      setError('Неуспешно зареждане на нотификациите');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(selectedAgent);
  }, [selectedAgent]);

  const markAsRead = async (notificationId: number) => {
    try {
      await CoachNotificationService.markAsRead(notificationId);
      setNotifications((prev) => prev.map((item) => (
        item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
      )));
    } catch (err) {
      console.error(err);
      setError('Неуспешно отбелязване като прочетена');
    }
  };

  const markAllAsRead = async () => {
    try {
      await CoachNotificationService.markAllAsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() })));
    } catch (err) {
      console.error(err);
      setError('Неуспешно отбелязване на всички като прочетени');
    }
  };

  const unreadCount = notifications.filter((notification) => !notification.isRead).length;
  const latestFatigue = selectedAgent === 'trainer' ? notifications.find((notification) => notification.type === 'fatigue_score') : null;
  const fatigueData = latestFatigue?.fatigue || null;

  const fatigueLevelLabel = (level: string): string => {
    if (level === 'high') return 'Висока';
    if (level === 'medium') return 'Средна';
    if (level === 'low') return 'Ниска';
    return 'Няма данни';
  };

  const fatigueLevelColor = (level: string): 'success' | 'warning' | 'error' | 'default' => {
    if (level === 'high') return 'error';
    if (level === 'medium') return 'warning';
    if (level === 'low') return 'success';
    return 'default';
  };

  const activeAgent = agents.find((a) => a.key === selectedAgent);
  const displayCoach: CoachProfile = activeAgent
    ? { name: activeAgent.displayName, tagline: activeAgent.tagline, avatarUrl: activeAgent.avatarUrl || undefined }
    : coach;

  return (
    <Box>
      {/* Agent selector */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {agents.map((agent) => (
          <Chip
            key={agent.key}
            label={agent.displayName}
            variant={selectedAgent === agent.key ? 'filled' : 'outlined'}
            color={selectedAgent === agent.key ? 'primary' : 'default'}
            onClick={() => setSelectedAgent(agent.key)}
            avatar={
              agent.avatarUrl
                ? <Avatar src={getImageUrl(agent.avatarUrl)} alt={agent.displayName} />
                : <Avatar>{agent.displayName.charAt(6)}</Avatar>
            }
          />
        ))}
      </Stack>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box
              sx={{
                width: { xs: 132, sm: 176 },
                height: { xs: 164, sm: 220 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                flexShrink: 0,
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1.25,
              }}
            >
              <Avatar
                src={displayCoach.avatarUrl ? getImageUrl(displayCoach.avatarUrl) : undefined}
                alt={displayCoach.name}
                variant="rounded"
                imgProps={{ loading: 'lazy' }}
                sx={{
                  width: '100%',
                  height: '100%',
                  fontSize: 48,
                  '& .MuiAvatar-img': {
                    objectFit: 'contain',
                  },
                }}
              >
                {displayCoach.name.charAt(6)}
              </Avatar>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight="bold">{displayCoach.name}</Typography>
              <Typography variant="body2" color="text.secondary">{displayCoach.tagline}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedAgent === 'trainer'
                  ? 'Тук ще получаваш автоматични анализи и препоръки на база тренировъчните ти логове.'
                  : 'Тук ще получаваш съвети за хранене и хранителен баланс.'}
              </Typography>
            </Box>
            <Chip
              icon={<NotificationsActiveIcon />}
              color={unreadCount > 0 ? 'warning' : 'success'}
              label={unreadCount > 0 ? `${unreadCount} нови` : 'Няма нови'}
            />
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">Нотификации</Typography>
        <Button variant="outlined" onClick={markAllAsRead} disabled={unreadCount === 0}>
          Маркирай всички като прочетени
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {selectedAgent === 'trainer' && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Fatigue Score</Typography>
                    <Typography variant="caption" color="text.secondary">Период: последни 7 дни</Typography>
                    {fatigueData ? (
                      <>
                        <Typography variant="h4" sx={{ mt: 0.5 }}>{fatigueData.score}/100</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {(fatigueData.factors || []).slice(0, 2).join(' ')}
                        </Typography>
                      </>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Все още няма изчислен fatigue score за последните 7 дни.
                      </Alert>
                    )}
                  </Box>
                  {fatigueData && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Chip
                        color={fatigueLevelColor(fatigueData.level)}
                        label={fatigueLevelLabel(fatigueData.level)}
                      />
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {notifications.length === 0 ? (
            <Alert severity="info">
              {selectedAgent === 'trainer'
                ? 'Все още няма съвети от твоя AI треньор.'
                : 'Няма нотификации от нутриционистa.'}
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {notifications.map((notification) => (
                <Card
                  key={notification.id}
                  variant="outlined"
                  sx={{
                    borderColor: notification.isRead ? 'divider' : 'warning.main',
                    backgroundColor: notification.isRead ? 'background.paper' : 'rgba(255, 167, 38, 0.08)',
                  }}
                >
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>{notification.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          {new Date(notification.createdAt).toLocaleString('bg-BG')}
                        </Typography>
                      </Box>
                      {!notification.isRead && (
                        <Button variant="contained" onClick={() => markAsRead(notification.id)}>
                          Прочетено
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}

      <Divider sx={{ mt: 3, mb: 2 }} />
      <Typography variant="caption" color="text.secondary">
        Съветите се генерират от скриптовете `trainer.sh` и `nutritionist.sh` и се записват като нотификации.
      </Typography>
    </Box>
  );
};

export default CoachNotifications;
